import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/lib/auth-context";
import { staffChatAPI } from "@/lib/api";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Send, 
  Loader2,
  MessageSquare,
  Users,
  Plus,
  Trash2
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function StaffChatPage() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  
  // For new conversation dialog
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [staffUsers, setStaffUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groupName, setGroupName] = useState("");

  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const pollConvsRef = useRef(null);

  const loadConversations = useCallback(async () => {
    try {
      const res = await staffChatAPI.listConversations();
      setConversations(res.data);
      if (!activeConvId && res.data.length > 0) {
        setActiveConvId(res.data[0].id);
      }
    } catch (error) {
      console.error("Failed to load conversations", error);
    } finally {
      setLoading(false);
    }
  }, [activeConvId]);

  const loadMessages = useCallback(async (convId, since = null) => {
    if (!convId) return;
    try {
      const response = await staffChatAPI.listMessages(convId, since);
      if (since) {
        setMessages(prev => {
          const newMsgs = response.data.filter(m => !prev.find(p => p.id === m.id));
          return [...prev, ...newMsgs];
        });
      } else {
        setMessages(response.data);
      }
    } catch (error) {
      console.error("Failed to load messages:", error);
    }
  }, []);

  const loadStaffUsers = async () => {
    try {
      const res = await staffChatAPI.listStaffUsers();
      // Filter out ourselves
      const staff = res.data.filter(u => u.id !== user?.id);
      setStaffUsers(staff);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadConversations();
    loadStaffUsers();
    
    // Poll for conversations updates every 10s
    pollConvsRef.current = setInterval(() => {
      loadConversations();
    }, 10000);

    return () => clearInterval(pollConvsRef.current);
  }, [loadConversations]);

  useEffect(() => {
    if (activeConvId) {
      loadMessages(activeConvId);
      
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      
      pollIntervalRef.current = setInterval(() => {
        setMessages(prev => {
          const lastMessage = prev[prev.length - 1];
          loadMessages(activeConvId, lastMessage?.created_at);
          return prev;
        });
      }, 2000); // Polling every 2 seconds for instant feel
    }

    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [activeConvId, loadMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConvId) return;

    setSending(true);
    try {
      const response = await staffChatAPI.sendMessage(activeConvId, {
        content: newMessage.trim()
      });
      setMessages(prev => [...prev, response.data]);
      setNewMessage("");
      loadConversations(); // refresh last_message
    } catch (error) {
      toast.error("Erreur d'envoi");
    } finally {
      setSending(false);
    }
  };

  const handleCreateConversation = async () => {
    if (selectedUsers.length === 0) return;
    
    const type = selectedUsers.length === 1 ? "private" : "group";
    try {
      const res = await staffChatAPI.createConversation({
        type,
        participant_ids: selectedUsers,
        name: type === "group" ? groupName : null
      });
      
      setShowNewDialog(false);
      setSelectedUsers([]);
      setGroupName("");
      
      await loadConversations();
      setActiveConvId(res.data.id);
    } catch (error) {
      toast.error("Erreur lors de la création");
    }
  };

  const handleDeleteConversation = async (convId) => {
    try {
      await staffChatAPI.deleteConversation(convId);
      toast.success("Conversation supprimée");
      if (activeConvId === convId) {
        setActiveConvId(null);
        setMessages([]);
      }
      await loadConversations();
    } catch (error) {
      const msg = error.response?.data?.detail || "Erreur lors de la suppression";
      toast.error(msg);
    }
  };

  const getConvName = (conv) => {
    if (conv.type === "group") return conv.name || "Groupe sans nom";
    const otherParticipant = conv.participants?.find(p => p.id !== user?.id);
    return otherParticipant ? `${otherParticipant.first_name} ${otherParticipant.last_name}` : "Utilisateur supprimé";
  };

  const activeConv = conversations.find(c => c.id === activeConvId);

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="flex h-[calc(100vh-4rem)] border rounded-xl overflow-hidden bg-background">
        {/* Sidebar Conversations */}
        <div className="w-80 border-r flex flex-col">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="font-semibold text-lg flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              Staff Chat
            </h2>
            
            <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Plus className="h-5 w-5" />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nouvelle conversation</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Sélectionner des collègues</label>
                    <div className="border rounded-md max-h-48 overflow-y-auto p-2 space-y-1">
                      {staffUsers.map(u => (
                        <div key={u.id} className="flex items-center gap-2 p-2 hover:bg-muted rounded-md cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={selectedUsers.includes(u.id)}
                            onChange={(e) => {
                              if (e.target.checked) setSelectedUsers([...selectedUsers, u.id]);
                              else setSelectedUsers(selectedUsers.filter(id => id !== u.id));
                            }}
                          />
                          <Avatar className="h-6 w-6">
                            <AvatarFallback>{u.first_name[0]}</AvatarFallback>
                          </Avatar>
                          <span className="text-sm">{u.first_name} {u.last_name}</span>
                          <span className="text-xs text-muted-foreground capitalize ml-auto">{u.role}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {selectedUsers.length > 1 && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Nom du groupe</label>
                      <Input 
                        placeholder="Ex: Équipe DevSecOps"
                        value={groupName}
                        onChange={(e) => setGroupName(e.target.value)}
                      />
                    </div>
                  )}

                  <Button 
                    className="w-full" 
                    onClick={handleCreateConversation}
                    disabled={selectedUsers.length === 0 || (selectedUsers.length > 1 && !groupName)}
                  >
                    Démarrer
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1">
              {conversations.length === 0 ? (
                <div className="text-center p-4 text-muted-foreground text-sm">
                  Aucune conversation
                </div>
              ) : (
                conversations.map(conv => (
                  <button
                    key={conv.id}
                    onClick={() => setActiveConvId(conv.id)}
                    className={`w-full text-left p-3 rounded-lg flex items-start gap-3 transition-colors ${
                      activeConvId === conv.id ? "bg-primary/10" : "hover:bg-muted"
                    }`}
                  >
                    <Avatar className="h-10 w-10 shrink-0">
                      {conv.type === "group" ? (
                        <AvatarFallback className="bg-primary/20"><Users className="h-5 w-5" /></AvatarFallback>
                      ) : (
                        <AvatarFallback>{getConvName(conv)[0]}</AvatarFallback>
                      )}
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-baseline mb-1">
                        <p className="text-sm font-medium truncate">{getConvName(conv)}</p>
                        {conv.last_message && (
                          <span className="text-xs text-muted-foreground whitespace-nowrap ml-2">
                            {new Date(conv.updated_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {conv.last_message ? conv.last_message.content : "Nouvelle conversation"}
                      </p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col bg-background/50">
          {activeConvId ? (
            <>
              {/* Chat Header */}
              <div className="h-16 border-b px-6 flex items-center bg-card">
                <Avatar className="h-8 w-8 mr-3">
                  {activeConv?.type === "group" ? (
                    <AvatarFallback className="bg-primary/20"><Users className="h-4 w-4" /></AvatarFallback>
                  ) : (
                    <AvatarFallback>{activeConv ? getConvName(activeConv)[0] : '?'}</AvatarFallback>
                  )}
                </Avatar>
                <div className="flex-1">
                  <h3 className="font-semibold text-sm">{activeConv ? getConvName(activeConv) : "Chargement..."}</h3>
                  {activeConv?.type === "group" && (
                    <p className="text-xs text-muted-foreground">
                      {activeConv.participants.length} membres
                    </p>
                  )}
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Supprimer cette conversation ?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Tous les messages seront définitivement supprimés. Cette action est irréversible.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        onClick={() => handleDeleteConversation(activeConvId)}
                      >
                        Supprimer
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>

              {/* Messages Area */}
              <ScrollArea className="flex-1 p-6">
                <div className="space-y-6">
                  {messages.map((message) => {
                    const isOwn = message.sender_id === user?.id;
                    return (
                      <div 
                        key={message.id}
                        className={`flex gap-3 max-w-[80%] ${isOwn ? "ml-auto flex-row-reverse" : ""}`}
                      >
                        {!isOwn && (
                          <Avatar className="h-8 w-8 shrink-0">
                            <AvatarFallback className="text-xs">
                              {message.sender_name.split(' ').map(n=>n[0]).join('').substring(0, 2)}
                            </AvatarFallback>
                          </Avatar>
                        )}
                        <div className={`flex flex-col ${isOwn ? "items-end" : "items-start"}`}>
                          {!isOwn && activeConv?.type === "group" && (
                            <span className="text-xs text-muted-foreground mb-1 ml-1">{message.sender_name}</span>
                          )}
                          <div 
                            className={`rounded-2xl px-4 py-2 ${
                              isOwn 
                                ? "bg-primary text-primary-foreground rounded-tr-sm" 
                                : "bg-muted rounded-tl-sm"
                            }`}
                          >
                            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                          </div>
                          <span className="text-[10px] text-muted-foreground mt-1">
                            {new Date(message.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Chat Input */}
              <div className="p-4 bg-card border-t">
                <form onSubmit={handleSend} className="flex gap-2">
                  <Input
                    placeholder="Tapez votre message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    disabled={sending}
                    className="flex-1 rounded-full px-4"
                  />
                  <Button 
                    type="submit" 
                    size="icon"
                    className="rounded-full shrink-0"
                    disabled={sending || !newMessage.trim()}
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
              <MessageSquare className="h-12 w-12 mb-4 opacity-20" />
              <p>Sélectionnez une conversation ou démarrez-en une nouvelle</p>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
